import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Eye, Package, Truck, Clock, CheckCircle, XCircle, Loader2, User, MapPin, Mail, Phone, Hash, HandMetal } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl ${className}`} {...props}>{children}</div>
);

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-500/10 text-yellow-300 border-yellow-500/20', icon: Clock },
  fulfilled: { label: 'Fulfilled', color: 'bg-green-500/10 text-[#00CC99] border-green-500/20', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-green-500/10 text-[#00CC99] border-green-500/20', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/10 text-[#FF6B6B] border-red-500/20', icon: XCircle },
  shipped: { label: 'Shipped', color: 'bg-blue-500/10 text-[#0075FF] border-blue-500/20', icon: Truck },
  personal_delivery: { label: 'Personal Delivery', color: 'bg-purple-500/10 text-[#A78BFA] border-purple-500/20', icon: HandMetal },
};

export default function DashboardOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showFulfillDialog, setShowFulfillDialog] = useState(false);
  const [fulfillOrder, setFulfillOrder] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [deliveryType, setDeliveryType] = useState('shipping');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, { headers });
      setOrders(response.data);
    } catch {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const updateOrderStatus = async (orderId, status, extra = {}) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { status, ...extra }, { headers });
      toast.success('Order updated');
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status, ...extra }));
      }
    } catch {
      toast.error('Failed to update order');
    }
  };

  const handleFulfillClick = (order) => {
    setFulfillOrder(order);
    setTrackingNumber(order.tracking_number || '');
    setDeliveryType(order.delivery_type || 'shipping');
    setShowFulfillDialog(true);
  };

  const handleFulfillSubmit = async () => {
    if (!fulfillOrder) return;
    const extra = { delivery_type: deliveryType };
    if (deliveryType === 'shipping' && trackingNumber.trim()) {
      extra.tracking_number = trackingNumber.trim();
      extra.status = 'shipped';
    } else if (deliveryType === 'personal_delivery') {
      extra.status = 'fulfilled';
      extra.delivery_type = 'personal_delivery';
    } else {
      extra.status = 'fulfilled';
    }
    await updateOrderStatus(fulfillOrder.id, extra.status, extra);
    setShowFulfillDialog(false);
    setFulfillOrder(null);
    setTrackingNumber('');
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) || order.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) || order.id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter || (statusFilter === 'fulfilled' && ['fulfilled', 'completed', 'shipped', 'personal_delivery'].includes(order.status));
    return matchesSearch && matchesStatus;
  });

  const statusTabs = [
    { key: 'all', label: 'All', count: orders.length },
    { key: 'pending', label: 'Pending', count: orders.filter(o => o.status === 'pending').length },
    { key: 'fulfilled', label: 'Fulfilled', count: orders.filter(o => ['fulfilled', 'completed', 'shipped', 'personal_delivery'].includes(o.status)).length },
    { key: 'cancelled', label: 'Cancelled', count: orders.filter(o => o.status === 'cancelled').length },
  ];

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 size={32} className="animate-spin text-[#8B5CF6]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="dashboard-orders">
      <div>
        <h1 className="text-2xl font-bold text-white" data-testid="orders-title">Orders</h1>
        <p className="text-[#9CA3AF] text-sm mt-1">{orders.length} total orders</p>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="order-status-tabs">
        {statusTabs.map((tab) => (
          <button key={tab.key} onClick={() => setStatusFilter(tab.key)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${statusFilter === tab.key
            ? 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)]'
            : 'bg-white/5 border border-white/10 text-[#9CA3AF] hover:text-white hover:bg-white/10'
          }`} data-testid={`tab-${tab.key}`}>
            {tab.label} <span className="ml-1.5 text-xs opacity-70">({tab.count})</span>
          </button>
        ))}
      </div>

      <div className="relative max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
        <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search orders..." className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="search-orders" />
      </div>

      {filteredOrders.length > 0 ? (
        <GlassCard className="overflow-hidden" data-testid="orders-table">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="px-6 py-3 text-xs uppercase tracking-wider text-[#64748B] font-medium">Order</th>
                  <th className="px-6 py-3 text-xs uppercase tracking-wider text-[#64748B] font-medium">Customer</th>
                  <th className="px-6 py-3 text-xs uppercase tracking-wider text-[#64748B] font-medium">Items</th>
                  <th className="px-6 py-3 text-xs uppercase tracking-wider text-[#64748B] font-medium">Total</th>
                  <th className="px-6 py-3 text-xs uppercase tracking-wider text-[#64748B] font-medium">Status</th>
                  <th className="px-6 py-3 text-xs uppercase tracking-wider text-[#64748B] font-medium">Date</th>
                  <th className="px-6 py-3 text-xs uppercase tracking-wider text-[#64748B] font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => {
                  const sc = statusConfig[order.status] || statusConfig.pending;
                  return (
                    <tr key={order.id} onClick={() => setSelectedOrder(order)} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors cursor-pointer" data-testid={`order-row-${order.id}`}>
                      <td className="px-6 py-4 font-mono text-xs text-[#94A3B8]">{order.id.slice(0, 8)}...</td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-white text-sm">{order.customer_name}</p>
                        <p className="text-xs text-[#64748B]">{order.customer_email}</p>
                      </td>
                      <td className="px-6 py-4 text-[#94A3B8]">{order.items?.length || 0}</td>
                      <td className="px-6 py-4 text-[#00CC99] font-medium">${order.total?.toFixed(2)}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full font-semibold border ${sc.color}`}>
                          <sc.icon size={12} /> {sc.label}
                        </span>
                        {order.tracking_number && <p className="text-[10px] text-[#64748B] mt-1">Tracking: {order.tracking_number}</p>}
                        {order.delivery_type === 'personal_delivery' && <p className="text-[10px] text-[#A78BFA] mt-1">Personal Delivery</p>}
                      </td>
                      <td className="px-6 py-4 text-[#94A3B8] text-xs">{formatDate(order.created_at)}</td>
                      <td className="px-6 py-4">
                        <button onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); }} className="p-2 text-[#9CA3AF] hover:text-white hover:bg-white/5 rounded-lg transition-colors" data-testid={`view-order-${order.id}`}>
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </GlassCard>
      ) : (
        <GlassCard className="text-center py-16">
          <Package size={48} className="mx-auto text-[#64748B] mb-4" />
          <h3 className="font-semibold text-lg text-white mb-2">No orders yet</h3>
          <p className="text-[#9CA3AF] text-sm">Orders will appear here when customers make purchases.</p>
        </GlassCard>
      )}

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl">Order Details</DialogTitle></DialogHeader>
          {selectedOrder && (
            <div className="space-y-5" data-testid="order-detail">
              {/* Customer Info Card */}
              <div className="bg-white/5 border border-white/5 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <User size={14} className="text-[#A78BFA]" />
                  <span className="text-sm font-medium text-white">Customer Information</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-start gap-2">
                    <User size={12} className="text-[#64748B] mt-0.5 shrink-0" />
                    <div><p className="text-[#64748B] text-xs mb-0.5">Name</p><p className="text-white">{selectedOrder.customer_name}</p></div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Mail size={12} className="text-[#64748B] mt-0.5 shrink-0" />
                    <div><p className="text-[#64748B] text-xs mb-0.5">Email</p><p className="text-[#94A3B8]">{selectedOrder.customer_email}</p></div>
                  </div>
                  {selectedOrder.customer_phone && (
                    <div className="flex items-start gap-2">
                      <Phone size={12} className="text-[#64748B] mt-0.5 shrink-0" />
                      <div><p className="text-[#64748B] text-xs mb-0.5">Phone</p><p className="text-[#94A3B8]">{selectedOrder.customer_phone}</p></div>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <Hash size={12} className="text-[#64748B] mt-0.5 shrink-0" />
                    <div><p className="text-[#64748B] text-xs mb-0.5">Order ID</p><p className="font-mono text-xs text-[#94A3B8]">{selectedOrder.id}</p></div>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              {selectedOrder.shipping_address && (
                <div className="bg-white/5 border border-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin size={14} className="text-[#A78BFA]" />
                    <span className="text-sm font-medium text-white">Shipping Address</span>
                  </div>
                  <p className="text-[#94A3B8] text-sm leading-relaxed">
                    {selectedOrder.shipping_address?.address && <>{selectedOrder.shipping_address.address}<br/></>}
                    {selectedOrder.shipping_address?.city && <>{selectedOrder.shipping_address.city}, </>}
                    {selectedOrder.shipping_address?.state && <>{selectedOrder.shipping_address.state} </>}
                    {selectedOrder.shipping_address?.zip && <>{selectedOrder.shipping_address.zip}<br/></>}
                    {selectedOrder.shipping_address?.country}
                  </p>
                </div>
              )}

              {/* Order Items */}
              <div>
                <p className="text-sm font-medium text-white mb-2">Items ({selectedOrder.items?.length || 0})</p>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between py-3 px-4 bg-white/5 rounded-xl border border-white/5">
                      <div className="flex items-center gap-3">
                        {item.image && <img src={item.image} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                        <div>
                          <p className="text-white text-sm font-medium">{item.name}</p>
                          <p className="text-xs text-[#64748B]">
                            {item.size && `Size: ${item.size}`}
                            {item.color && ` | Color: ${item.color}`}
                            {` | Qty: ${item.quantity}`}
                          </p>
                        </div>
                      </div>
                      <p className="text-[#00CC99] text-sm font-medium">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-between items-center pt-3 border-t border-white/5">
                <span className="text-[#9CA3AF]">Total</span>
                <span className="text-xl font-bold text-white">${selectedOrder.total?.toFixed(2)}</span>
              </div>

              {/* Delivery Info */}
              {(selectedOrder.tracking_number || selectedOrder.delivery_type === 'personal_delivery') && (
                <div className="bg-white/5 border border-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Truck size={14} className="text-[#A78BFA]" />
                    <span className="text-sm font-medium text-white">Delivery Info</span>
                  </div>
                  {selectedOrder.delivery_type === 'personal_delivery' ? (
                    <p className="text-[#A78BFA] text-sm">Personal Delivery - Delivered in person</p>
                  ) : selectedOrder.tracking_number ? (
                    <p className="text-[#94A3B8] text-sm">Tracking: <span className="font-mono text-white">{selectedOrder.tracking_number}</span></p>
                  ) : null}
                </div>
              )}

              {/* Status Actions */}
              <div className="space-y-3 pt-3 border-t border-white/5">
                <p className="text-[#64748B] text-xs uppercase tracking-wider font-medium">Update Status</p>
                <div className="grid grid-cols-2 gap-2">
                  {selectedOrder.status === 'pending' && (
                    <>
                      <button onClick={() => handleFulfillClick(selectedOrder)} className="py-2.5 rounded-xl text-sm font-semibold bg-[#00CC99]/10 text-[#00CC99] border border-[#00CC99]/20 hover:bg-[#00CC99]/20 transition-colors flex items-center justify-center gap-2" data-testid="fulfill-order-btn">
                        <CheckCircle size={14} /> Mark Fulfilled
                      </button>
                      <button onClick={() => updateOrderStatus(selectedOrder.id, 'cancelled')} className="py-2.5 rounded-xl text-sm font-semibold bg-[#FF6B6B]/10 text-[#FF6B6B] border border-[#FF6B6B]/20 hover:bg-[#FF6B6B]/20 transition-colors flex items-center justify-center gap-2" data-testid="cancel-order-btn">
                        <XCircle size={14} /> Cancel Order
                      </button>
                    </>
                  )}
                  {['fulfilled', 'shipped', 'personal_delivery'].includes(selectedOrder.status) && (
                    <button onClick={() => handleFulfillClick(selectedOrder)} className="col-span-2 py-2.5 rounded-xl text-sm font-semibold bg-[#8B5CF6]/10 text-[#A78BFA] border border-[#8B5CF6]/20 hover:bg-[#8B5CF6]/20 transition-colors flex items-center justify-center gap-2" data-testid="update-tracking-btn">
                      <Truck size={14} /> Update Delivery Info
                    </button>
                  )}
                  {selectedOrder.status === 'cancelled' && (
                    <button onClick={() => updateOrderStatus(selectedOrder.id, 'pending')} className="col-span-2 py-2.5 rounded-xl text-sm font-semibold bg-yellow-500/10 text-yellow-300 border border-yellow-500/20 hover:bg-yellow-500/20 transition-colors flex items-center justify-center gap-2" data-testid="reopen-order-btn">
                      <Clock size={14} /> Reopen Order
                    </button>
                  )}
                </div>
                <p className="text-xs text-[#64748B]">Date: {formatDate(selectedOrder.created_at)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fulfill Order Dialog */}
      <Dialog open={showFulfillDialog} onOpenChange={setShowFulfillDialog}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="text-lg">Fulfill Order</DialogTitle></DialogHeader>
          <div className="space-y-5 mt-2">
            <p className="text-sm text-[#9CA3AF]">How is this order being delivered?</p>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setDeliveryType('shipping')} className={`p-4 rounded-xl border text-left transition-all ${deliveryType === 'shipping' ? 'bg-[#8B5CF6]/10 border-[#8B5CF6]/40 shadow-[0_0_15px_rgba(139,92,246,0.1)]' : 'bg-white/5 border-white/10 hover:border-white/20'}`} data-testid="delivery-type-shipping">
                <Truck size={20} className={deliveryType === 'shipping' ? 'text-[#A78BFA]' : 'text-[#64748B]'} />
                <p className="text-sm font-medium text-white mt-2">Shipping</p>
                <p className="text-xs text-[#64748B] mt-0.5">Add tracking number</p>
              </button>
              <button onClick={() => setDeliveryType('personal_delivery')} className={`p-4 rounded-xl border text-left transition-all ${deliveryType === 'personal_delivery' ? 'bg-[#8B5CF6]/10 border-[#8B5CF6]/40 shadow-[0_0_15px_rgba(139,92,246,0.1)]' : 'bg-white/5 border-white/10 hover:border-white/20'}`} data-testid="delivery-type-personal">
                <HandMetal size={20} className={deliveryType === 'personal_delivery' ? 'text-[#A78BFA]' : 'text-[#64748B]'} />
                <p className="text-sm font-medium text-white mt-2">Personal Delivery</p>
                <p className="text-xs text-[#64748B] mt-0.5">Delivered in person</p>
              </button>
            </div>

            {deliveryType === 'shipping' && (
              <div>
                <Label className="text-[#9CA3AF] text-xs">Tracking Number</Label>
                <Input value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} placeholder="Enter tracking number" className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl mt-1" data-testid="tracking-input" />
              </div>
            )}

            {deliveryType === 'personal_delivery' && (
              <div className="bg-[#A78BFA]/10 border border-[#A78BFA]/20 rounded-xl p-3">
                <p className="text-xs text-[#A78BFA]">This order will be marked as personally delivered. No tracking number needed.</p>
              </div>
            )}

            <button onClick={handleFulfillSubmit} className="w-full py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] transition-all text-sm" data-testid="confirm-fulfill-btn">
              {deliveryType === 'personal_delivery' ? 'Mark as Personal Delivery' : trackingNumber.trim() ? 'Ship with Tracking' : 'Mark as Fulfilled'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
